import type {
  AudioRecCalibrationProfile,
  AudioRecRecognitionHints,
  AudioRecNoteTemplate,
  RecognitionPeak,
} from '../types';
import { hzToMidi } from '../util/note';
import { clamp } from '../calibration/stats';
import { isApproxHarmonic, isSamePitchHz } from './pitch';

const MAX_PROFILED_NOTES = 4;
const MAX_PROFILED_HARMONIC = 6;

interface TemplateSupport {
  template: AudioRecNoteTemplate;
  direct: number;
  harmonic: number;
  alias: number;
  total: number;
}

const computeTemplateSupport = (
  template: AudioRecNoteTemplate,
  rawPeaks: RecognitionPeak[],
  noisePenaltyHzs: number[],
): TemplateSupport => {
  const strongestAmp = rawPeaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
  if (!Number.isFinite(strongestAmp) || strongestAmp <= 0) {
    return { template, direct: 0, harmonic: 0, alias: 0, total: 0 };
  }

  const limitedPeaks = rawPeaks.slice(0, 6);
  let direct = 0;
  let harmonic = 0;
  let alias = 0;
  const matchedHarmonics = new Set<number>();

  for (const peak of limitedPeaks) {
    const normalizedAmp = peak.amp / strongestAmp;

    if (isSamePitchHz(peak.hz, template.fundamentalHz, 55)) {
      direct = Math.max(direct, normalizedAmp * 1.2);
    }

    for (const harmonicTemplate of template.harmonics) {
      if (harmonicTemplate.harmonic <= 1) continue;
      const expectedHz = template.fundamentalHz * harmonicTemplate.harmonic;
      if (!isSamePitchHz(peak.hz, expectedHz, 60)) continue;
      matchedHarmonics.add(harmonicTemplate.harmonic);
      harmonic +=
        normalizedAmp *
        Math.max(0.25, harmonicTemplate.amplitudeRatio) *
        (0.55 + harmonicTemplate.coverage * 0.75);
    }

    const peakMidi = Math.round(hzToMidi(peak.hz));
    const aliasTemplate = template.aliases.find((value) => value.midi === peakMidi);
    if (aliasTemplate) {
      alias += normalizedAmp * aliasTemplate.strength * 0.45;
    }
  }

  const noisePenalty = noisePenaltyHzs.some((noiseHz) =>
    isSamePitchHz(noiseHz, template.fundamentalHz, 40),
  )
    ? 0.2
    : 0;

  return {
    template,
    direct,
    harmonic,
    alias,
    total: direct * 1.35 + harmonic + alias * 0.35 + matchedHarmonics.size * 0.12 - noisePenalty,
  };
};

const dedupeCandidates = (candidates: RecognitionPeak[], maxNotes: number) => {
  const deduped: RecognitionPeak[] = [];
  for (const candidate of candidates) {
    if (deduped.some((existing) => isSamePitchHz(existing.hz, candidate.hz, 35))) continue;
    deduped.push(candidate);
    if (deduped.length >= maxNotes) break;
  }
  return deduped;
};

export const applyCalibrationProfileBias = (
  profile: AudioRecCalibrationProfile | null,
  rawPeaks: RecognitionPeak[],
  candidates: RecognitionPeak[],
  hints: AudioRecRecognitionHints | null,
  maxNotes = MAX_PROFILED_NOTES,
) => {
  if (!profile || profile.noteTemplates.length === 0) {
    return dedupeCandidates(candidates, maxNotes);
  }

  const expectedMidiNotes = new Set(hints?.expectedMidiNotes ?? []);
  const noisePenaltyHzs = profile.roomProfile.noisePeaks.map((peak) => peak.hz);
  const templateSupports = profile.noteTemplates
    .map((template) => computeTemplateSupport(template, rawPeaks, noisePenaltyHzs))
    .sort((a, b) => b.total - a.total || a.template.midi - b.template.midi);

  const rescored: RecognitionPeak[] = [];
  for (const candidate of candidates) {
    const candidateMidi = Math.round(hzToMidi(candidate.hz));
    let score = candidate.score ?? 0;
    let suppress = false;

    if (expectedMidiNotes.has(candidateMidi)) {
      score += 0.4;
    }

    for (const support of templateSupports) {
      const templateMidi = support.template.midi;
      const matchesTemplateFundamental =
        templateMidi === candidateMidi &&
        isSamePitchHz(candidate.hz, support.template.fundamentalHz, 75);

      if (matchesTemplateFundamental) {
        score += support.total * 0.9 + support.template.sampleCount * 0.05;
        continue;
      }

      const aliasMatch = support.template.aliases.find((alias) => alias.midi === candidateMidi);
      const upperAlias =
        candidate.hz > support.template.fundamentalHz &&
        isApproxHarmonic(candidate.hz, support.template.fundamentalHz, MAX_PROFILED_HARMONIC, 40);
      const lowerAlias =
        candidate.hz < support.template.fundamentalHz &&
        isApproxHarmonic(support.template.fundamentalHz, candidate.hz, MAX_PROFILED_HARMONIC, 40);

      if (!aliasMatch && !upperAlias && !lowerAlias) continue;

      const aliasStrength = aliasMatch?.strength ?? (upperAlias || lowerAlias ? 0.75 : 0);
      score -= support.total * aliasStrength * 0.95;

      if (support.total >= 1.05 && aliasStrength >= 0.35) {
        suppress = true;
      }
    }

    if (!suppress || expectedMidiNotes.has(candidateMidi)) {
      rescored.push({ ...candidate, score });
    }
  }

  const merged = rescored.slice();
  const strongestAmp = rawPeaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
  for (const support of templateSupports) {
    const expectedBoost = expectedMidiNotes.has(support.template.midi) ? 0.45 : 0;
    const alreadyPresent = merged.some((candidate) =>
      isSamePitchHz(candidate.hz, support.template.fundamentalHz, 35),
    );
    if (alreadyPresent) continue;

    const enoughSupport =
      support.direct >= 0.16 ||
      support.harmonic >= 1.0 ||
      (support.harmonic >= 0.72 && support.alias >= 0.2) ||
      (expectedBoost > 0 && support.total >= 0.62);
    if (!enoughSupport) continue;

    merged.push({
      hz: support.template.fundamentalHz,
      amp: strongestAmp * clamp(Math.max(0.12, support.direct, support.harmonic * 0.32), 0.08, 1),
      score: support.total + 0.42 + expectedBoost,
    });
  }

  return dedupeCandidates(
    merged.sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || b.amp - a.amp || a.hz - b.hz),
    maxNotes,
  );
};
